import { useCallback, useEffect, useRef } from "react";

/** History state marker for the system-back trap entry. */
export const SYSTEM_BACK_TRAP_KEY = "__traSystemBackTrap";

function isTrapState(state) {
  return Boolean(state && typeof state === "object" && state[SYSTEM_BACK_TRAP_KEY]);
}

function clearTrapFlag() {
  if (!isTrapState(window.history.state)) return;
  const next = { ...window.history.state };
  delete next[SYSTEM_BACK_TRAP_KEY];
  window.history.replaceState(next, "");
}

/**
 * Binds the browser / Android system back control to the same handler as the
 * in-app top-left back button.
 *
 * Pushes a same-URL history trap so the first hardware/gesture back stays in
 * the app and invokes `onBack` (including fallback when there is no prior route).
 *
 * @param {(() => void) | null | undefined} onBack
 * @param {boolean} [enabled=true]
 * @returns {() => void} Click handler for the UI back control (clears the trap first)
 */
export function useSystemBackButton(onBack, enabled = true) {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const armedRef = useRef(false);

  const runBack = useCallback(() => {
    onBackRef.current?.();
  }, []);

  /**
   * UI back: drop the same-URL trap with replaceState, then run onBack.
   * history.back() on that trap stays on this URL and the router reloads
   * the page instead of leaving (Exit and the public Back chevron).
   */
  const handleBack = useCallback(() => {
    if (typeof window !== "undefined" && isTrapState(window.history.state)) {
      clearTrapFlag();
      armedRef.current = false;
    }
    runBack();
  }, [runBack]);

  useEffect(() => {
    if (!enabled) return undefined;

    const prev = window.history.state;
    const base =
      prev && typeof prev === "object" && !Array.isArray(prev) ? { ...prev } : {};
    window.history.pushState({ ...base, [SYSTEM_BACK_TRAP_KEY]: true }, "");
    armedRef.current = true;

    const onPopState = () => {
      armedRef.current = false;
      runBack();
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      // Clear marker only — do not history.back() (races with NavLink navigations)
      if (armedRef.current && isTrapState(window.history.state)) {
        clearTrapFlag();
        armedRef.current = false;
      }
    };
  }, [enabled, runBack]);

  return handleBack;
}
